import _ from 'underscore';
import { Subject } from '@bingads-webui-universal/observer-pattern';
import { TimeAwareCache } from '@bingads-webui-universal/time-aware-cache';

import { StandardMatchStrategy } from './match-strategy/index';
import { PartitionStrategy } from './partition-strategy/index';
import { ClientCacheManager } from './client-cache-manager';
import { ServerCacheManager } from './server-cache-manager';

import { BUILTIN_ACTIONS } from './constants';

/**
 * Decorate a JSData adapter for a certain cache manager
 * @private
 * @param {JSDataCacheManager} cacheManager - The cache manager
 * @param {string} name - Name of the adapter
 * @param {WrapperCallback} wrapper - The wrapper callback used to decorate the adapter
 * @returns {void}
 */
function decorateAdapter(cacheManager, name, wrapper) {
  const { store, actions } = cacheManager;
  const adapter = store.adapters[name];

  /**
   * @private
   * @callback {WrapperCallback}
   * @param {JSDataAdapter} adapter - The adapter to decorate
   * @returns {MethodWrapperCallback} - The callback to wrap adapter methods
   */
  const methodWrapper = wrapper(adapter);

  store.adapters[name] = _.reduce(actions, (memo, action) => {
    if (_.isFunction(adapter[action])) {
      _.extend(memo, {
        /**
         * @private
         * @callback {MethodWrapperCallback}
         * @param {string} action - The action name
         * @returns {Function} - The wrapped adapter method
         */
        [action]: methodWrapper(action),
      });
    }
    return memo;
  }, {});
}

/**
 * Monitor all actions of a certain adapter for a cache manager
 * @private
 * @param {JSDataCacheManager} cacheManager - The JSDataCacheManager instance
 * @param {string} name - The name of the adapter
 * @returns {void}
 */
function monitorAdapter(cacheManager, name) {
  decorateAdapter(cacheManager, name, adapter => action => (...parameters) => {
    /**
     * @typedef {Object} BeforeExecuteParameters
     * @property {string} action - The action being execute
     * @property {string} adapter - The name of the adapter
     * @property {Object[]} parameters - The action parameters
     */
    const eventParams = {
      action,
      adapter: name,
      parameters,
    };

    // Send 'beforeExecute' notification
    cacheManager.notify('beforeExecute', eventParams);
    const result = adapter[action](...parameters);

    /**
     * @typedef {Object} AfterExecuteParameters
     * @property {string} action - The action being execute
     * @property {string} adapter - The name of the adapter
     * @property {Object[]} parameters - The action parameters
     * @property {boolean} isSuccess - If the action was succeeded
     * @property {Object} data - The data returned by the action on success
     * @property {Error} error - The error on failure
     */
    result.then((data) => {
      // Send 'afterExecute' notification on success
      cacheManager.notify('afterExecute', _.defaults({
        isSuccess: true,
        data,
      }, eventParams));
    }, (error) => {
      // Send 'afterExecute' notification on failure
      cacheManager.notify('afterExecute', _.defaults({
        isSuccess: false,
        error,
      }, eventParams));
    });

    return result;
  });
}

/**
 * Monitor all the adapters for a JSDataCacheManager
 * @private
 * @param {JSDataCacheManager} cacheManager - The JSDataCacheManager instance
 * @returns {void}
 */
function monitorAdapters(cacheManager) {
  const { store } = cacheManager;
  const registerAdapter = store.registerAdapter.bind(store);

  store.registerAdapter = (name, adapter, options) => {
    registerAdapter(name, adapter, options);
    monitorAdapter(cacheManager, name);
  };

  _.each(_.keys(store.adapters), _.partial(monitorAdapter, cacheManager));
}

export class JSDataCacheManager extends Subject {
  /**
   * Cache manager coupling with a JSData Store to manage both client side and
   * server side cache.
   * @param {Object} options -
   * @param {JSData.DS} options.store - The JSData data store
   * @param {string[]} [options.actions=[]] - Addtional custom JSData actions
   * @param {InvalidateStrategy[]} [options.invalidateStrategies=[]] -
   *    Invalidate strategies which observe the cache manager actions and
   *    invalidates cache items based on its customized logic
   * @param {MatchStrategy[]} [options.matchStrategies=[]] -
   *    Custom match strategy for the client side cache to reuse cached values
   *    with a similar cache key. The standard match strategy, which does the
   *    exact match, will always be prepended to the list.
   * @param {PartitionStrategy[]} [options.partitionStrategies] -
   *    Client side cache partition strategy. It can be used to assign separated
   *    quota for certain queries. The default value is a single item array with
   *    an unlimited partition for all cache entries
   */
  constructor({
    store,
    actions = [],
    invalidateStrategies = [],
    matchStrategies = [],
    partitionStrategies = [
      new PartitionStrategy({ cache: new TimeAwareCache() }),
    ],
  }) {
    super();
    this.store = store;
    this.actions = _.union(actions, BUILTIN_ACTIONS);

    // Initialize the client side cache manager
    this.clientCacheManager = new ClientCacheManager({
      matchStrategies: [new StandardMatchStrategy()].concat(matchStrategies),
      partitionStrategies,
    });

    // Initialize the server side cache manager
    this.serverCacheManager = new ServerCacheManager();

    // Initialize the invalidate strategies
    this.invalidateStrategies = invalidateStrategies;
    _.each(invalidateStrategies, strategy => strategy.initializeCacheManager(this));

    monitorAdapters(this);
  }

  /**
   * Enable cache on a given adapter. When cache is enabled on an adapter, all
   * the action methods will receive 5 cache related callbacks through options.
   * @param {string} name - The name of the adapter
   * @param {AdapterDecoratorCallback} decorator - A decorative function to
   *    modify the behavior of a JSData adatper
   * @returns {void}
   */
  enableCacheOnAdapter(name, decorator) {
    decorateAdapter(this, name, (adapter) => {
      /**
       * To decorate an JSData adapter. The object returned can be a partial
       * adapter containing only the modified methods. For actions not included
       * in the decorated adatper, they will fallback to the original adapter.
       * @callback AdapterDecoratorCallback
       * @param {JSDataAdapter} adapter - The original adapter
       * @returns {JSDataAdapter} - The decorated adapter
       */
      const decoratedAdapter = decorator(adapter);

      return (action) => {
        const selectedAdapter = _.find([
          decoratedAdapter,
          adapter,
        ], _.compose(_.isFunction, _.property(action)));

        return (definition, ...args) => {
          const resource = definition.name;
          const scope = { resource, adapter: name };
          const resultKey = query => _.extend({ query }, scope);

          return selectedAdapter[action](definition, ..._.initial(args), _.defaults({
            /**
             * Get cached result for a query
             * @param {Object} query - The query
             * @returns {*} - The cached result, null for cache miss.
             */
            getResult: query => this.getResult(resultKey(query)),

            /**
             * Set cached result for a query
             * @param {Object} query - The query
             * @param {*} result - The query result
             * @param {number} ttl - The life cycle of this cached result
             * @returns {void}
             */
            setResult: (query, result, ttl) => this.setResult(resultKey(query), result, ttl),

            /**
             * Delete cached result for a query
             * @param {Object} query - The query
             * @returns {void}
             */
            delResult: query => this.delResult(resultKey(query)),

            /**
             * Get server side cache hint
             * @returns {*} - Server side cache hint
             */
            getCacheHint: () => this.getCacheHint(scope),

            /**
             * Set server side cache hint
             * @param {*} hint - Server side cache hint
             * @param {number} ttl - The life cycle of this cache hint
             * @returns {void}
             */
            setCacheHint: (hint, ttl) => this.setCacheHint(scope, hint, ttl),

            /**
             * Delete server side cache hint
             * @returns {*} - Server side cache hint
             */
            delCacheHint: () => this.delCacheHint(scope),

            /**
             * Wrap the data fetching callback to use client side cache. Return
             * the cached result on cache hit. Save the result in cache.
             * @param {Object} query - The query object
             * @param {FetchCallback} callback - The callback to fetch data
             * @param {number} ttl - The life time of the cached data
             * @returns {*} result - The fetched data
             */
            cacheResult: (query, callback, ttl) => {
              const key = resultKey(query);
              const result = this.getResult(key) || callback(query);

              this.setResult(key, result, ttl);
              return result;
            },
          }, _.last(args)));
        };
      };
    });
  }

  /**
   * Get the cached data
   * @param {ClientCacheKey} key - The cache key
   * @returns {*} - The cached data, null on cache miss
   */
  getResult(key) {
    return this.clientCacheManager.match(key);
  }

  /**
   * Set the cached data for given key
   * @param {ClientCacheKey} key - The cache key
   * @param {*} result - The data associated with the key
   * @param {number} ttl - The life time of the cache entry
   * @returns {*} - The cached value
   */
  setResult(key, result, ttl) {
    return this.clientCacheManager.set(key, result, ttl);
  }

  /**
   * Delete the cached data
   * @param {ClientCacheKey} key - The cache key
   * @returns {void}
   */
  delResult(key) {
    return this.clientCacheManager.del(key);
  }

  /**
   * Get the server cache hint
   * @param {ServerCacheKey} key - The cache key
   * @returns {*} - The server cache hint, null on cache miss
   */
  getCacheHint(key) {
    return this.serverCacheManager.get(key);
  }

  /**
   * Set the server side cache hint for given key
   * @param {ServerCacheKey} key - The cache key
   * @param {*} hint - The server cache hint associated with the key
   * @param {number} ttl - The life time of the cache entry
   * @returns {*} - The cache hint
   */
  setCacheHint(key, hint, ttl) {
    return this.serverCacheManager.set(key, hint, ttl);
  }

  /**
   * Get the server cache hint
   * @param {ServerCacheKey} key - The cache key
   * @returns {*} - The server cache hint, null on cache miss
   */
  delCacheHint(key) {
    return this.serverCacheManager.del(key);
  }

  /**
   * Invalidate both client side and server side cache for given resource
   * @param {string} resource - Name of the resoruce (entity type) to invalidate
   * @param {bool} isTriggeredFromOutside - Indicate whether the invalidate is from outside
   * @returns {void}
   */
  invalidate(resource, isTriggeredFromOutside = false) {
    this.notify('beforeInvalidate', resource);
    this.clientCacheManager.invalidate(resource);
    this.serverCacheManager.invalidate(resource);
    this.notify('afterInvalidate', resource, isTriggeredFromOutside);
  }
}
